import React from 'react';
import { withRouter, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  FormControl,
  FormLabel,
  Input,
  Link,
  FormErrorMessage,
  CircularProgress,
} from '@chakra-ui/react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Button } from '../';
import { useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';
import { doSignup, closeModal, openModal } from '../../redux/reducers';
import { InfoModal } from '../';
import { InfoModalMode } from '../common/modal/infoModal';

const CreateUserForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showModal, modalProps } = useSelector((state) => state.ui);
  const { newUserError } = useSelector((state) => state.user);
  // initializing form schema
  const SignupSchema = Yup.object().shape({
    firstname: Yup.string()
      .max(100, t('createUser.form.invalidName'))
      .required(t('createUser.form.required')),
    lastname: Yup.string()
      .max(100, t('createUser.form.invalidName'))
      .required(t('createUser.form.required')),
    email: Yup.string()
      .email(t('createUser.form.invalidEmail'))
      .required(t('createUser.form.required')),
    password: Yup.string()
      .min(8, t('createUser.form.passwordMinLength'))
      .matches(RegExp('^(?=.*[A-Z])(?=.*[a-z])(?=.*\\d).*$'), t('createUser.form.passwordStrength'))
      .required(t('createUser.form.required')),
    passwordCheck: Yup.string()
      .oneOf([Yup.ref('password'), null], t('createUser.form.passwordMatch'))
      .min(8, t('createUser.form.passwordMinLength'))
      .required(t('createUser.form.required')),
  });
  const handleSubmit = (signupData, actions) => {
    dispatch(doSignup(signupData)).then((result) => {
      actions.setSubmitting(false);
      // error in login
      if (result === undefined) {
        dispatch(openModal());
      }
    });
  };
  return (
    <>
      <InfoModal
        open={showModal === true}
        mode={InfoModalMode.Error}
        bodyText={newUserError?.message}
        headerText={t('modal.failedRequest')}
        rightButtonText={t('modal.ok')}
        leftButtonText={null}
        onClose={() => dispatch(closeModal())}
        onRightButtonClick={() => dispatch(closeModal())}
        {...modalProps}
      />
      <Formik
        initialValues={{
          firstname: '',
          lastname: '',
          email: '',
          password: '',
          passwordCheck: '',
        }}
        validationSchema={SignupSchema}
        onSubmit={(data, actions) => handleSubmit(data, actions)}
      >
        {({ isSubmitting }) => (
          <Form>
            <Field name="firstname">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.firstname && form.touched.firstname}
                  mt={16}
                  id="firstname"
                  isRequired
                  colorScheme="gray"
                >
                  <FormLabel color="white" htmlFor="firstname">
                    {t('createUser.form.firstName')}
                  </FormLabel>
                  <Input
                    {...field}
                    color="white"
                    variant="flushed"
                    type="fname"
                    autoComplete="off"
                    id="firstname"
                  />
                  <FormErrorMessage>{form.errors.firstname}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field name="lastname">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.lastname && form.touched.lastname}
                  mt={4}
                  id="lastname"
                  isRequired
                  colorScheme="gray"
                >
                  <FormLabel color="white" htmlFor="lastname">
                    {t('createUser.form.lastName')}
                  </FormLabel>
                  <Input
                    {...field}
                    color="white"
                    variant="flushed"
                    type="lname"
                    autoComplete="off"
                    id="lastname"
                  />
                  <FormErrorMessage>{form.errors.lastname}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field name="email">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.email && form.touched.email}
                  mt={4}
                  id="email"
                  isRequired
                >
                  <FormLabel color="white" htmlFor="email">
                    {t('createUser.form.email')}
                  </FormLabel>
                  <Input
                    {...field}
                    color="white"
                    variant="flushed"
                    type="email"
                    id="email"
                    autoComplete="off"
                  />
                  <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field name="password">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.password && form.touched.password}
                  mt={4}
                  id="password"
                  isRequired
                >
                  <FormLabel color="white" htmlFor="password">
                    {t('createUser.form.password')}
                  </FormLabel>
                  <Input
                    {...field}
                    color="white"
                    variant="flushed"
                    type="password"
                    autoComplete="new-password"
                    id="password"
                  />
                  <FormErrorMessage>{form.errors.password}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field name="passwordCheck">
              {({ field, form }) => (
                <FormControl
                  isInvalid={form.errors.passwordCheck && form.touched.passwordCheck}
                  mt={4}
                  id="passwordCheck"
                  isRequired
                >
                  <FormLabel color="white" htmlFor="passwordCheck">
                    {t('createUser.form.passwordCheck')}
                  </FormLabel>
                  <Input
                    {...field}
                    color="white"
                    variant="flushed"
                    type="password"
                    autoComplete="new-password"
                    id="passwordCheck"
                  />
                  <FormErrorMessage>{form.errors.passwordCheck}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Button
              mt={16}
              color="white"
              borderWidth={2}
              borderColor="white"
              backgroundColor="brand.50"
              type="submit"
              width="100%"
              disabled={isSubmitting}
            >
              {isSubmitting === false && t('createUser.signUp')}
              {isSubmitting === true && <CircularProgress isIndeterminate color="white" size={8} />}
            </Button>
          </Form>
        )}
      </Formik>
      <Link mt={8} color="white" as={RouterLink} to="/login">
        {t('createUser.signIn')}
      </Link>
    </>
  );
};

export default withRouter(CreateUserForm);
